import { useState, useEffect, useMemo, useCallback } from "react";
import { Box, InputAdornment, Stack, CircularProgress, Typography, Tooltip, Checkbox, useMediaQuery, useTheme } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useTranslate } from "src/locales";
import { referenceService } from 'src/api/references/referenceService';
import { GetPatientProcessResponseDto } from "src/api/references/referenceServicesDtos";
import { dateTimeFormatter } from "src/utils/date";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiPrintButton from "src/components/core-components/mui/buttons/ByzMuiPrintButton";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useGridPrint } from "src/hooks/useGridPrint";
import { GridColDef, GridRenderCellParams, GridToolbarContainer, GridSortModel } from "@mui/x-data-grid";
import ByzDataTreeListGrid from "src/components/core-components/mui/data-grid/ByzDataTreeListGrid";
import { useParams } from "react-router-dom";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import RequestServiceModal from "../../patient-list/pages/list/service-process/request-service/index";
import AddServiceModal from "../../patient-list/pages/list/service-process/add-service/index";
import RequestList from "../../patient-list/pages/list/service-process/request-list/index";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    PATIENT_PROCESS_DEFAULT_PAGE_SIZE,
    PATIENT_PROCESS_PAGE_SIZE_OPTIONS,
} from "../patientProcessGridConfig";

export interface PatientProcessProps {
    ptId?: string;
    upn?: string;
    /** Hasta tablosu: üstte PatientDatas yok, tam ekran yüksekliği yok */
    embedded?: boolean;
    /** embedded iken modal başlıkları (hasta özeti yerine) */
    patientMeta?: {
        displayName?: string;
        department?: string;
        upn?: string;
    };
}

interface ProcessRow {
    id: number;
    parentId: number | null;
    processName: string;
    categoryName: string;
    processTime: string;
    officialCode: string;
    doctorName: string;
    quantity: number;
    processTypeName: string;
    creatorUser: string;
}

const ProcessCustomToolbar = ({
    searchTerm,
    onSearchChange,
    onPrint,
    onAddService,
    onRequestService,
    onOpenRequestList
}: {
    searchTerm: string;
    onSearchChange: (val: string) => void;
    onPrint: () => void;
    onAddService: () => void;
    onRequestService: () => void;
    onOpenRequestList: () => void;
}) => {
    const { t } = useTranslate();
    const { t: tCommon } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <GridToolbarContainer
            sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
            }}
        >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} sx={{ flex: 1, width: "100%" }}>
                <ByzMuiTextField
                    placeholder={t("patient-process.search-process")}
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    size="small"
                    autoComplete="off"
                    sx={{
                        width: { xs: "100%", sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '24px',
                            backgroundColor: '#f8fafc',
                            '& fieldset': {
                                border: 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                            },
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Search sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ width: "100%" }}>
                    <ByzMuiButton
                        variant2="outlined"
                        onClick={onAddService}
                        sx={{ height: 40, borderRadius: '8px', textTransform: 'none', px: 2, flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 auto" } }}
                    >
                        {tCommon("menus.add-service")}
                    </ByzMuiButton>
                    <ByzMuiButton
                        variant2="outlined"
                        onClick={onRequestService}
                        sx={{ height: 40, borderRadius: '8px', textTransform: 'none', px: 2, flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 auto" } }}
                    >
                        {tCommon("menus.request-service")}
                    </ByzMuiButton>
                    <ByzMuiButton
                        variant2="outlined"
                        onClick={onOpenRequestList}
                        sx={{ height: 40, borderRadius: '8px', textTransform: 'none', px: 2, flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 auto" } }}
                    >
                        {tCommon("menus.completed-requests")}
                    </ByzMuiButton>
                    <Tooltip title={tCommon("under-development")}>
                        <span style={{ display: 'inline-block', flex: isMobile ? '1 1 calc(50% - 4px)' : undefined }}>
                            <ByzMuiButton
                                variant2="outlined"
                                disabled
                                sx={{ height: 40, borderRadius: '8px', textTransform: 'none', px: 2, width: { xs: "100%", sm: "auto" } }}
                            >
                                {tCommon("menus.add-stock")}
                            </ByzMuiButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={tCommon("under-development")}>
                        <span style={{ display: 'inline-block', flex: isMobile ? '1 1 calc(50% - 4px)' : undefined }}>
                            <ByzMuiButton
                                variant2="outlined"
                                disabled
                                sx={{ height: 40, borderRadius: '8px', textTransform: 'none', px: 2, width: { xs: "100%", sm: "auto" } }}
                            >
                                {tCommon("menus.request-stock")}
                            </ByzMuiButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-end", sm: "flex-start" }} sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Box sx={{ height: 32, width: '1px', bgcolor: 'divider', mx: 1, display: { xs: "none", sm: "block" } }} />
                <ByzMuiPrintButton
                    type={1}
                    onClick={onPrint}
                    sx={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        p: 0,
                        '& img': {
                            width: '24px !important',
                            height: '24px !important',
                            maxWidth: 'none !important',
                            maxHeight: 'none !important'
                        }
                    }}
                />
            </Stack>
        </GridToolbarContainer>
    );
}

const buildTree = (flatRows: ProcessRow[]) => {
    const tree: any[] = [];
    const map = new Map<number, any>();

    flatRows.forEach(row => {
        map.set(row.id, { ...row, children: [] });
    });

    flatRows.forEach(row => {
        const node = map.get(row.id);
        if (row.parentId && map.has(row.parentId)) {
            map.get(row.parentId).children.push(node);
        } else {
            tree.push(node);
        }
    });

    return tree;
};

const flattenTree = (nodes: any[], result: ProcessRow[] = []) => {
    nodes.forEach(node => {
        const { children, ...row } = node;
        result.push(row);
        if (children && children.length > 0) {
            flattenTree(children, result);
        }
    });
    return result;
};

const PatientProcess = (props: PatientProcessProps) => {
    const { id } = useParams<{ id: string }>();
    const embedded = props.embedded === true;

    const mainTabs = useSelector((state: any) => state.tabs.mainTabs);
    const activeTabIndex = useSelector((state: any) => state.tabs.activeTabIndex);
    const activeTab = mainTabs[activeTabIndex];
    const reduxPtId = activeTab?.data?.patientId;

    const effectivePtId = embedded ? props.ptId : (props.ptId || id || reduxPtId);

    const [rows, setRows] = useState<ProcessRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [patientLoading, setPatientLoading] = useState(!embedded && !!effectivePtId);
    const [patientInfo, setPatientInfo] = useState<any>(null);

    const [selectedIds, setSelectedIds] = useState<any[]>([]);
    const [openAddService, setOpenAddService] = useState(false);
    const [openRequestService, setOpenRequestService] = useState(false);
    const [openRequestList, setOpenRequestList] = useState(false);

    const { t } = useTranslate();

    const handleDelete = useCallback((row: any) => {
        console.log("Silinecek satır:", row);
    }, []);

    const toggleRow = useCallback((rowId: number, checked: boolean) => {
        const getChildIds = (parentId: number): number[] => {
            const children = rows.filter(r => r.parentId === parentId);
            return children.reduce((acc, child) => [...acc, child.id, ...getChildIds(child.id)], [] as number[]);
        };

        const targetIds = [rowId, ...getChildIds(rowId)];

        if (checked) {
            setSelectedIds(prev => Array.from(new Set([...prev, ...targetIds])));
        } else {
            setSelectedIds(prev => prev.filter(id => !targetIds.includes(id)));
        }
    }, [rows]);

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "selection",
            headerName: "",
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Checkbox
                    size="small"
                    checked={selectedIds.includes(params.row.id)}
                    onChange={(e) => toggleRow(params.row.id, e.target.checked)}
                    sx={{ p: 0 }}
                />
            )
        },
        {
            field: "grid_actions",
            headerName: "",
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <ByzMuiOpenProcessMenu
                    menuItems={[
                        {
                            label: t("delete"),
                            disabled: true,
                            tooltip: t("under-development"),
                            onClick: () => handleDelete(params.row)
                        }
                    ]}
                    hideHeaderTitle={true}
                    inSearch={false}
                    sx={{ '& .MuiIconButton-root': { p: 0.5 } }}
                />
            )
        },
        { field: "tree_indicator", headerName: "", width: 60, sortable: false, filterable: false },
        { field: "processTime", headerName: t("patient-process.process-time"), flex: 1 },
        { field: "categoryName", headerName: t("patient-process.category-name"), flex: 1.2 },
        {
            field: "processName",
            headerName: t("patient-process.process-name"),
            flex: 2,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontWeight: (params.row.parentId === null || params.row.parentId === undefined) ? 'bold' : 'normal',
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        { field: "officialCode", headerName: t("patient-process.official-code"), flex: 1 },
        { field: "doctorName", headerName: t("patient-process.doctor-name"), flex: 1.5 },
        { field: "quantity", headerName: t("patient-process.quantity"), flex: 0.5, type: 'number' },
        { field: "processTypeName", headerName: t("patient-process.process-type"), flex: 1 },
        { field: "creatorUser", headerName: t("patient-process.creator-user"), flex: 1 },
    ], [t, selectedIds, handleDelete, toggleRow]);

    const sortTreeData = useCallback((data: ProcessRow[], sortModel: GridSortModel) => {
        if (!sortModel || sortModel.length === 0) return data;

        const { field, sort } = sortModel[0];
        if (!sort) return data;

        const tree = buildTree(data);

        const sortRecursive = (nodes: any[]) => {
            nodes.sort((a: any, b: any) => {
                const valA = a[field] || "";
                const valB = b[field] || "";

                if (valA < valB) return sort === 'asc' ? -1 : 1;
                if (valA > valB) return sort === 'asc' ? 1 : -1;
                return 0;
            });

            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    sortRecursive(node.children);
                }
            });
        };

        sortRecursive(tree);
        return flattenTree(tree);

    }, []);

    const handleSortModelChange = useCallback((model: GridSortModel) => {
        setRows(prevRows => sortTreeData([...prevRows], model));
    }, [sortTreeData]);

    const fetchPatientProcesses = useCallback(async () => {
        if (!effectivePtId) return;

        setLoading(true);
        try {
            const response = await referenceService.patientProcess.getPatientProcessList({
                PtId: Number(effectivePtId)
            });

            if (!response?.data || response.data.length === 0) {
                setRows([]);
                return;
            }

            const sortNodes = (nodes: GetPatientProcessResponseDto[]) => {
                nodes.sort((a, b) => {
                    const timeA = a.processtime || "";
                    const timeB = b.processtime || "";
                    if (timeA < timeB) return 1; // Changed to descending
                    if (timeA > timeB) return -1; // Changed to descending
                    return 0;
                });

                nodes.forEach(node => {
                    if (node.children && node.children.length > 0) {
                        sortNodes(node.children);
                    }
                });
            };

            sortNodes(response.data);

            const flattenedRows: ProcessRow[] = [];

            const flatten = (items: GetPatientProcessResponseDto[]) => {
                items.forEach((item) => {
                    flattenedRows.push({
                        id: item.id,
                        parentId: item.parentid,
                        processName: item.processname || "",
                        categoryName: item.categoryname || "",
                        processTime: item.processtime ? dateTimeFormatter(item.processtime) : "",
                        officialCode: item.officialcode || "",
                        doctorName: item.doctorname || "",
                        quantity: item.quantity,
                        processTypeName: item.processtypename || "",
                        creatorUser: item.creatoruser,
                    });
                    if (item.children && item.children.length > 0) {
                        flatten(item.children);
                    }
                });
            };

            flatten(response.data);
            setRows(flattenedRows);
        } catch (error) {
            console.error("Error fetching patient processes:", error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [effectivePtId]);

    useEffect(() => {
        if (effectivePtId) {
            setPatientLoading(true);
            fetchPatientProcesses();
        }
    }, [effectivePtId, fetchPatientProcesses]);

    const { printGrid } = useGridPrint();

    const handlePatientLoadingChange = useCallback((isLoading: boolean) => {
        setPatientLoading(isLoading);
    }, []);

    const handlePrint = useCallback(() => {
        printGrid({
            rows: rows,
            columns: columns,
            title: t("patient-process.title"),
            idField: "id",
            parentIdField: "parentId",
            indentColumn: "processName"
        });
    }, [rows, columns, t, printGrid]);

    const ToolbarComp = useCallback((toolbarProps: any) => (
        <ProcessCustomToolbar
            {...toolbarProps}
            onAddService={() => setOpenAddService(true)}
            onRequestService={() => setOpenRequestService(true)}
            onOpenRequestList={() => setOpenRequestList(true)}
        />
    ), []); // Stable identity

    const isPageLoading = loading || (!embedded && patientLoading);

    const modalPatientTitle =
        props.patientMeta?.displayName?.trim() ||
        `${patientInfo?.name || ""} ${patientInfo?.surname || ""}`.trim();
    const modalDept = props.patientMeta?.department ?? patientInfo?.deptname ?? "";
    const modalUpn =
        props.patientMeta?.upn != null && props.patientMeta.upn !== ""
            ? String(props.patientMeta.upn)
            : (patientInfo?.upn ? String(patientInfo.upn) : "");

    const gridCard = (
        <Box
            sx={{
                flex: 1,
                minHeight: embedded ? 320 : 0,
                width: "100%",
                bgcolor: "white",
                borderRadius: embedded ? "12px" : 1,
                boxShadow: embedded ? "0px 1px 8px rgba(0, 0, 0, 0.08)" : 1,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {embedded && loading && (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.72)",
                    }}
                >
                    <CircularProgress size={40} />
                </Stack>
            )}
            <ByzDataTreeListGrid
                initialData={rows}
                columns={columns}
                loading={false}
                nameField="processName"
                idField="id"
                parentIdField="parentId"
                autoHeight={false}
                whiteGrid={true}
                pagination={true}
                showDefaultToolbarButtons={false}
                useLConnector={true}
                useSubdirectoryArrow={false}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                pageSizeOptions={[...PATIENT_PROCESS_PAGE_SIZE_OPTIONS]}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "processTime", sort: "desc" }],
                    },
                    pagination: {
                        paginationModel: { pageSize: PATIENT_PROCESS_DEFAULT_PAGE_SIZE, page: 0 },
                    },
                }}
                customToolbar={ToolbarComp}
                onPrint={handlePrint}
                sx={{
                    height: "100%",
                    width: "100%",
                    minHeight: embedded ? 320 : undefined,
                    "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
                        display: "none !important",
                    },
                }}
                treeColumn="tree_indicator"
                indentColumns={["selection", "grid_actions", "tree_indicator"]}
                checkboxSelection={true}
                onSelectionChange={setSelectedIds}
                selectedIds={selectedIds}
                isActionColumnShow={false}
            />
        </Box>
    );

    const modals = (
        <>
            <AddServiceModal
                open={openAddService}
                onClose={() => {
                    setOpenAddService(false);
                    fetchPatientProcesses();
                }}
                title={modalPatientTitle}
                description={modalDept}
                rowID={effectivePtId || ""}
                upn={modalUpn}
            />

            <RequestServiceModal
                open={openRequestService}
                onClose={() => {
                    setOpenRequestService(false);
                    fetchPatientProcesses();
                }}
                title={modalPatientTitle}
                description={modalDept}
                rowID={effectivePtId || ""}
                upn={modalUpn}
            />

            <RequestList
                open={openRequestList}
                onClose={() => setOpenRequestList(false)}
                ptId={effectivePtId || ""}
                upn={modalUpn}
                title={modalPatientTitle}
                description={modalDept}
            />
        </>
    );

    if (embedded) {
        return (
            <>
                {gridCard}
                {modals}
            </>
        );
    }

    return (
        <Box sx={{ height: "calc(100vh - 120px)", width: "100%", py: 2, pl: 2, pr: 0, position: "relative" }}>
            {isPageLoading && (
                <Stack
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        height: "100%",
                        width: "100%",
                        gap: 2,
                        position: "absolute",
                        zIndex: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                    }}
                >
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                        {t("loading")}
                    </Typography>
                </Stack>
            )}

            <Box
                sx={{
                    visibility: isPageLoading ? "hidden" : "visible",
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <PatientDatas
                    patientId={effectivePtId}
                    isOpenMedCard={true}
                    isCreateMedCard={true}
                    onLoadingChange={handlePatientLoadingChange}
                    patientDataOut={(data) => setPatientInfo(data?.data)}
                />
                {gridCard}
            </Box>

            {modals}
        </Box>
    );
};

export default PatientProcess;
